import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import useFloatingPosition from '../hooks/useFloatingPosition';
import QuickLaunchItem from './QuickLaunchItem';

function QuickLaunch({
  apps,
  editMode,
  onAdd,
  onRemove,
  onReorder,
  onEdit
}) {
  const [width, setWidth] = useState(0);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const regionRef = useRef(null);
  const overflowButtonRef = useRef(null);
  const overflowRef = useRef(null);
  const closeOverflow = useCallback(() => setOverflowOpen(false), []);
  const overflowStyle = useFloatingPosition({
    open: overflowOpen,
    anchorRef: overflowButtonRef,
    floatRef: overflowRef,
    onDismiss: closeOverflow
  });

  useEffect(() => {
    if (!regionRef.current) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(regionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!overflowOpen) return undefined;
    const onDown = (event) => {
      if (!overflowButtonRef.current?.contains(event.target) && !overflowRef.current?.contains(event.target)) {
        closeOverflow();
      }
    };
    document.addEventListener('mousedown', onDown);
    overflowRef.current?.querySelector('a, button')?.focus({ preventScroll: true });
    return () => document.removeEventListener('mousedown', onDown);
  }, [overflowOpen, closeOverflow]);

  const visibleCount = useMemo(() => {
    const itemWidth = 48;
    const extra = (editMode ? 42 : 0) + (apps.length > 0 ? 42 : 0);
    const rawCapacity = Math.max(0, Math.floor((width - extra) / itemWidth));
    if (rawCapacity >= apps.length) return apps.length;
    return Math.max(0, Math.floor((width - extra - 42) / itemWidth));
  }, [width, editMode, apps.length]);
  const visible = apps.slice(0, visibleCount);
  const hidden = apps.slice(visibleCount);
  const itemIds = apps.map((app) => `quick-${app.id}`);

  return (
    <div className="quick-launch-region" ref={regionRef}>
      <SortableContext items={itemIds} strategy={horizontalListSortingStrategy}>
        <div className="quick-launch-dock" aria-label="Quick Launch">
          {visible.map((app) => (
            <QuickLaunchItem
              key={app.id}
              app={app}
              editMode={editMode}
              onEdit={() => onEdit?.(app)}
              onRemove={() => onRemove(app.id)}
            />
          ))}
          {hidden.length > 0 && (
            <button
              type="button"
              ref={overflowButtonRef}
              className="quick-launch-overflow-button"
              aria-label={`Show ${hidden.length} more applications`}
              aria-haspopup="menu"
              aria-expanded={overflowOpen}
              onClick={() => setOverflowOpen((value) => !value)}
            >
              <span aria-hidden="true">···</span>
              <span className="quick-launch-overflow-count">{hidden.length}</span>
            </button>
          )}
          {editMode && (
            <button type="button" className="quick-launch-add" onClick={onAdd} aria-label="Add to Quick Launch">
              +
            </button>
          )}
        </div>
        {overflowOpen && hidden.length > 0 && createPortal(
          <div
            className="quick-launch-overflow-menu"
            role="menu"
            ref={overflowRef}
            style={overflowStyle}
            onKeyDown={(event) => {
              const items = Array.from(overflowRef.current.querySelectorAll('a, button'));
              const current = items.indexOf(document.activeElement);
              if (event.key === 'Escape') {
                event.preventDefault();
                closeOverflow();
                overflowButtonRef.current?.focus();
              } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                items[Math.min(current + 1, items.length - 1)]?.focus();
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                items[Math.max(current - 1, 0)]?.focus();
              } else if (event.key === 'Tab') {
                closeOverflow();
              }
            }}
          >
            {hidden.map((app) => (
              <QuickLaunchItem
                key={app.id}
                app={app}
                editMode={editMode}
                overflow
                onEdit={() => onEdit?.(app)}
                onRemove={() => onRemove(app.id)}
              />
            ))}
          </div>,
          document.body
        )}
      </SortableContext>
    </div>
  );
}

export default QuickLaunch;